/**
 * Settings page (user + tenant scopes).
 * - Reads /settings/bundle (public cascade) and /settings/definitions.
 * - Groups by scope ('user.*' vs 'tenant.*' vs 'platform.*').
 * - In-place editor per key with the right input type for boolean/enum/string/number.
 * - Tenant settings hidden if user lacks `settings:write:tenant`.
 */
import React, { useMemo, useState } from 'react'
import { PageHeader } from '@/components/composites/PageHeader'
import { FormSkeleton } from '@/components/composites'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { usePermissions } from '@/hooks/usePermissions'
import {
  useSettingsBundle,
  useSettingDefinitions,
  useSettingMutations,
} from '../api/settings'
import type { SettingDefinition } from '@shared/settings/definitions'

export const SettingsPage = (): JSX.Element => {
  const { data: bundle, isLoading } = useSettingsBundle()
  const { data: defs = [] } = useSettingDefinitions()
  const { hasPermission } = usePermissions()
  const canWriteTenant = hasPermission('settings:write:tenant')
  const canWriteUser = hasPermission('settings:write:user')

  const effectiveByKey = useMemo(() => {
    const map = new Map<string, { value: unknown; source: string }>()
    for (const e of bundle?.effective ?? []) map.set(e.key, e)
    return map
  }, [bundle])

  if (isLoading) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Settings"
          description="System defaults → tenant overrides → personal preferences."
        />
        <Card>
          <CardContent className="pt-6">
            <FormSkeleton fields={4} />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <FormSkeleton fields={3} />
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Settings"
        description="System defaults → tenant overrides → personal preferences."
      />

      <SectionCard
        title="My preferences"
        scopeLabel="user"
        defs={defs.filter((d) => d.writable.includes('user'))}
        effectiveByKey={effectiveByKey}
        canWrite={canWriteUser}
        scope="user"
      />

      <SectionCard
        title="Tenant settings"
        scopeLabel="tenant"
        defs={defs.filter((d) => d.writable.includes('tenant'))}
        effectiveByKey={effectiveByKey}
        canWrite={canWriteTenant}
        scope="tenant"
      />

      <SectionCard
        title="System (read-only)"
        scopeLabel="system"
        defs={defs.filter((d) => d.writable.includes('system'))}
        effectiveByKey={effectiveByKey}
        canWrite={false}
        scope="system"
      />
    </div>
  )
}

function SectionCard({
  title,
  scopeLabel,
  defs,
  effectiveByKey,
  canWrite,
  scope,
}: {
  title: string
  scopeLabel: string
  defs: SettingDefinition[]
  effectiveByKey: Map<string, { value: unknown; source: string }>
  canWrite: boolean
  scope: 'user' | 'tenant' | 'system'
}): JSX.Element {
  if (defs.length === 0) return <></>
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          {title}
          <Badge variant="outline">{scopeLabel}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="divide-y">
        {defs.map((def) => {
          const eff = effectiveByKey.get(def.key)
          return (
            <SettingRow
              key={def.key}
              def={def}
              currentValue={eff?.value ?? def.default}
              source={eff?.source ?? 'default'}
              canWrite={canWrite}
              scope={scope}
            />
          )
        })}
      </CardContent>
    </Card>
  )
}

function SettingRow({
  def,
  currentValue,
  source,
  canWrite,
  scope,
}: {
  def: SettingDefinition
  currentValue: unknown
  source: string
  canWrite: boolean
  scope: 'user' | 'tenant' | 'system'
}): JSX.Element {
  const [draft, setDraft] = useState<unknown>(currentValue)
  const [error, setError] = useState<string | null>(null)
  const { setUser, setTenant, setSystem } = useSettingMutations()
  const dirty = !shallowEqual(draft, currentValue)
  const saving =
    (scope === 'user' && setUser.isPending) ||
    (scope === 'tenant' && setTenant.isPending) ||
    (scope === 'system' && setSystem.isPending)

  const save = async (): Promise<void> => {
    setError(null)
    try {
      const payload = { key: def.key, value: coerce(def, draft) }
      if (scope === 'user') await setUser.mutateAsync(payload)
      else if (scope === 'tenant') await setTenant.mutateAsync(payload)
      else await setSystem.mutateAsync(payload)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed')
    }
  }

  return (
    <div className="py-3 grid grid-cols-1 md:grid-cols-[1fr_2fr_auto] gap-3 items-start">
      <div className="min-w-0">
        <div className="text-sm font-medium font-mono break-all">{def.key}</div>
        <div className="text-xs text-muted-foreground">{def.description}</div>
        <div className="mt-1 flex flex-wrap gap-2">
          <Badge variant="outline">{def.type}</Badge>
          <Badge variant="secondary">source: {source}</Badge>
        </div>
      </div>

      <div className="min-w-0">
        <ValueInput
          def={def}
          value={draft}
          onChange={setDraft}
          disabled={!canWrite}
        />
        {error && (
          <Alert variant="destructive" className="mt-2">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
      </div>

      <div>
        {canWrite && (
          <Button size="sm" onClick={save} disabled={!dirty || saving} className="w-full md:w-auto">
            Save
          </Button>
        )}
      </div>
    </div>
  )
}

function ValueInput({
  def,
  value,
  onChange,
  disabled,
}: {
  def: SettingDefinition
  value: unknown
  onChange: (v: unknown) => void
  disabled: boolean
}): JSX.Element {
  switch (def.type) {
    case 'boolean':
      return (
        <Checkbox
          checked={!!value}
          disabled={disabled}
          onCheckedChange={(v) => onChange(!!v)}
        />
      )
    case 'enum':
      return (
        <Select
          value={String(value ?? '')}
          onValueChange={(v) => onChange(v)}
          disabled={disabled}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(def.enumValues ?? []).map((v) => (
              <SelectItem key={v} value={v}>
                {v}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )
    case 'number':
      return (
        <Input
          type="number"
          value={String(value ?? '')}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value === '' ? 0 : Number(e.target.value))}
        />
      )
    case 'object':
      return (
        <Input
          value={typeof value === 'object' ? JSON.stringify(value) : String(value ?? '')}
          disabled={disabled}
          onChange={(e) => {
            try {
              onChange(JSON.parse(e.target.value))
            } catch {
              onChange(e.target.value)
            }
          }}
        />
      )
    default:
      return (
        <Input
          value={String(value ?? '')}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
        />
      )
  }
}

function coerce(def: SettingDefinition, value: unknown): unknown {
  if (def.type === 'number' && typeof value === 'string') return Number(value)
  if (def.type === 'boolean' && typeof value !== 'boolean') return Boolean(value)
  return value
}

function shallowEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true
  return JSON.stringify(a) === JSON.stringify(b)
}
